import {isLightColor} from '#core/utils/color-utils.js';
import {assignRecursively, ensureHierarchy, getObjectProperty} from '#core/utils/config-utils.js';
import {Logger} from '#core/utils/logger.js';

import {DebugFlags} from '#core/application/debug/debugFlags.js';
import {ApplicationConfig} from '#core/config/application-config.js';

import {tileSchemaById} from '../schema-registry.js';

import {BasePalette} from './base-palette.js';
import {PaletteRetriever} from './palette-retriever.js';

const tileCacheLog = Logger('TileCache');
const cache = new Map();

export function constructTileConfigConsideringDarkLight(config, isLightBg) {
    if (isLightBg === (BasePalette.palette.DEFAULT_TILE_COLOR_SCHEME === 'light')) {
        // isLightBg matches default, so no need to specify variant
        return config;
    } else {
        const variantId = isLightBg ? 'ON_LIGHT' : 'ON_DARK';

        return assignRecursively({}, config, config[variantId] || {}); // Return a new object
    }
}

const EMPTY = Object.create(null);
export function constructTilePalette(id, logger, basedOnValue, extendWith) {
    const schema = tileSchemaById[id];

    if (!schema) {
        throw new Error(`Unknown tile ${id}`);
    }

    let cacheKey;
    if (!extendWith) {
        cacheKey = basedOnValue ? `${id}/${basedOnValue}` : id;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey); // Just return from the cache
        }
    }

    let variantId;
    if (basedOnValue && schema.darkLightVariants && BasePalette.palette.DEFAULT_TILE_COLOR_SCHEME !== 'none') {
        const isLightBg = isLightColor(basedOnValue, false);
        if (isLightBg === (BasePalette.palette.DEFAULT_TILE_COLOR_SCHEME === 'light')) {
            // isLightBg matches default, so no need to specify variant
            variantId = undefined;
        } else {
            variantId = isLightBg ? 'ON_LIGHT' : 'ON_DARK';
        }
    }

    let configToReadFrom;
    if (extendWith || variantId) {
        configToReadFrom = Object.create(null);
        const data = ensureHierarchy(configToReadFrom, schema.key);
        assignRecursively(data, getObjectProperty(ApplicationConfig.config, schema.key, EMPTY));
        if (variantId in data) {
            assignRecursively(data, data[variantId]);
        }
        if (extendWith) {
            assignRecursively(data, extendWith);
        }
    } else {
        configToReadFrom = ApplicationConfig.config;
    }

    let retriever = new PaletteRetriever(logger || tileCacheLog, {
        defaults: BasePalette.palette,
        defaultsResolved: BasePalette.palette,
        basedOn: basedOnValue,
    });
    retriever.doAnalyzeConfigs = DebugFlags.analyzeConfigs;
    const value = retriever.constructPaletteBySchema(configToReadFrom, schema);

    cacheKey && cache.set(cacheKey, value);

    return value;
}

export function clearTileCache() {
    cache.clear();
}

window.DBX = {...window.DBX, tileCache: cache};
